import { MarketCategory } from '@vertex-protocol/react-client';
import { ARCA_INSTITUTIONAL_MARKET_CATEGORIES } from 'client/utils/markets/arcaInstitutionalMarkets';
import { useCallback } from 'react';

type InstitutionalMarketCategory =
  (typeof ARCA_INSTITUTIONAL_MARKET_CATEGORIES)[number];

type MarketCategoryID = InstitutionalMarketCategory | 'all';

type MarketCategoryFilterByID = Record<
  MarketCategoryID,
  { value: MarketCategory | undefined; label: string }
>;

const marketCategoryFilterById: MarketCategoryFilterByID = {
  all: {
    value: undefined,
    label: 'All Markets',
  },
  commodity: {
    value: 'commodity',
    label: 'Commodities',
  },
  spot: {
    value: 'spot',
    label: 'Spot',
  },
  equities: {
    value: 'equities',
    label: 'Equities',
  },
  forex: {
    value: 'forex',
    label: 'FX',
  },
  index: {
    value: 'index',
    label: 'Indices',
  },
};

export interface UseMarketCategoryFilterParams {
  marketCategory: MarketCategory | undefined;
  setMarketCategory: (newCategory?: MarketCategory) => void;
}

export function useMarketCategoryFilter({
  marketCategory,
  setMarketCategory,
}: UseMarketCategoryFilterParams) {
  // Handle transformation from value to id
  const selectedMarketCategoryId: MarketCategoryID =
    marketCategory != null &&
    ARCA_INSTITUTIONAL_MARKET_CATEGORIES.includes(
      marketCategory as InstitutionalMarketCategory,
    )
      ? (marketCategory as InstitutionalMarketCategory)
      : 'all';

  const setSelectedMarketCategoryId = useCallback(
    (marketCategoryId: string) => {
      // Handle transformation from id to value
      const newMarketCategory =
        marketCategoryFilterById[marketCategoryId as MarketCategoryID];

      setMarketCategory(newMarketCategory?.value);
    },
    [setMarketCategory],
  );

  return {
    selectedMarketCategoryId,
    setSelectedMarketCategoryId,
    marketCategoryFilterById,
  };
}
